import axios from 'axios'
import store from '@/store'

const serverURL = process.env.VUE_APP_MESSKURS_API_URL || 'http://127.0.0.1:3010'

const apiClient = axios.create({
    baseURL: serverURL,
    withCredentials: false,
    headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
    },
    timeout: 15000
})

apiClient.interceptors.request.use(function (config) {
    if (!config.skipOverlay) {
        store.commit('setLoading', true)
    }
    return config
})

apiClient.interceptors.response.use(function (response) {
    store.commit('setLoading', false)
    return response
}, function (error) {
    store.commit('setLoading', false)
    return Promise.reject(error)
})

function normalizeSessionResults(session) {
    if (!session) return session
    if (session.results && typeof session.results === 'object') {
        return {
            ...session,
            results: JSON.stringify(session.results)
        }
    }
    return session
}

function normalizeSessionListResults(sessions) {
    if (!Array.isArray(sessions)) return sessions
    return sessions.map(normalizeSessionResults)
}

export default {
    health() {
        return apiClient.get('/api/health').then(res => res.data)
    },
    healthDeep() {
        return apiClient.get('/api/health/deep').then(res => res.data)
    },
    /* Sessions */
    async createSession(session) {
        return apiClient.post("/api/mm-sessions", session).then(result => {
            return normalizeSessionResults(result.data)
        }).catch(error => {
            console.error("Error creating session:", error);
            return null;
        });
    },
    async updateSession(session) {
        return apiClient.put("/api/mm-sessions/" + session._id, session).then(result => {
            return normalizeSessionResults(result.data)
        }).catch(error => {
            console.error("Error updating session:", error);
            return null;
        });
    },
    async updateSessionResults(sessionId, results) {
        const updateData = { results: JSON.stringify(results) };

        let session = JSON.parse(localStorage.getItem('session') || '{}');
        if (session && session._id === sessionId) {
            Object.assign(updateData, session);
            updateData.results = JSON.stringify(results);
        }

        return apiClient.put("/api/mm-sessions/" + sessionId, updateData, { skipOverlay: true }).then(result => {
            return normalizeSessionResults(result.data)
        }).catch(error => {
            console.error("Error updating session results:", error);
            if (error.response && error.response.data) {
                console.error("Validation details:", JSON.stringify(error.response.data, null, 2));
            }
            throw error;
        });
    },
    async finishSession(sessionId) {
        const updateData = { testFinished: true };
        const session = JSON.parse(localStorage.getItem('session') || '{}');
        if (session && session._id === sessionId) {
            Object.assign(updateData, session);
            updateData.testFinished = true;
        }

        return apiClient.put("/api/mm-sessions/" + sessionId, updateData).then(result => {
            return normalizeSessionResults(result.data)
        }).catch(error => {
            console.error("Error finishing session:", error);
            if (error.response && error.response.data) {
                console.error("Validation details:", JSON.stringify(error.response.data, null, 2));
            }
            throw error;
        });
    },
    async reenableSession(sessionId) {
        const updateData = { testFinished: false };
        const session = JSON.parse(localStorage.getItem('session') || '{}');
        if (session && session._id === sessionId) {
            Object.assign(updateData, session);
            updateData.testFinished = false;
        }

        return apiClient.put("/api/mm-sessions/" + sessionId, updateData).then(result => {
            return normalizeSessionResults(result.data)
        }).catch(error => {
            console.error("Error re-enabling session:", error);
            if (error.response && error.response.data) {
                console.error("Validation details:", JSON.stringify(error.response.data, null, 2));
            }
            throw error;
        });
    },
    async deleteSession(session) {
        return apiClient.delete("/api/mm-sessions/" + session).then(result => {
            return result.data
        }).catch(error => {
            console.error("Error deleting session:", error);
            return null;
        });
    },
    async getSession(session) {
        return apiClient.get("/api/mm-sessions/" + session, { skipOverlay: true }).then(result => {
            return normalizeSessionResults(result.data)
        }).catch(error => {
            console.error("Error fetching session:", error);
            return null;
        });
    },
    async getSessionsForInstructor(inst) {
        return apiClient.get('/api/mm-sessions?q={"instructor":"' + inst + '"}', { skipOverlay: true }).then(async result => {
            return normalizeSessionListResults(result.data)
        }).catch(error => {
            console.error("Error fetching sessions for instructor:", error);
            return null;
        });
    },
    async getSessionsForUser(user) {
        return apiClient.get('/api/mm-sessions?q={"user":"' + user + '"}', { skipOverlay: true }).then(async result => {
            return normalizeSessionListResults(result.data)
        }).catch(error => {
            console.error("Error fetching sessions for user:", error);
            return null;
        });
    },
    async getSessionsForObject(objectId) {
        return apiClient.get('/api/mm-sessions?q={"object":"' + objectId + '"}', { skipOverlay: true }).then(async result => {
            return normalizeSessionListResults(result.data)
        }).catch(error => {
            console.error("Error fetching sessions for object:", error);
            return null;
        });
    },
    async getObjectWithId(objectId) {
        return apiClient.get("/api/mm-objects/" + objectId, { skipOverlay: true }).then(result => {
            return result.data
        }).catch(error => {
            console.error("Error fetching object with ID " + objectId + ":", error.response ? error.response.status : error.message);
            return null;
        });
    },
    async getObjectsByQuery(query = {}) {
        const encodedQuery = encodeURIComponent(JSON.stringify(query));
        return apiClient.get(`/api/mm-objects?q=${encodedQuery}`).then(async result => {
            for (let object of result.data) {
                object.items = await this.getItemsForObject(object._id);
            }
            return result.data
        }).catch(error => {
            console.error("Error fetching objects by query:", error);
            return [];
        });
    },
    async getObjectsForOwner(owner) {
        return this.getObjectsByQuery({ owner: String(owner) });
    },
    async getObjectsForCreatedBy(createdBy) {
        return this.getObjectsByQuery({ createdBy: String(createdBy) });
    },
    async getObjectsForMesskursScope(ownerIds = [], createdById = "", legacyOwnerId = "") {
        const owners = (Array.isArray(ownerIds) ? ownerIds : [ownerIds]).map(String).filter(Boolean);
        const createdBy = String(createdById || "");
        const legacyOwner = String(legacyOwnerId || "");

        const conditions = [];
        owners.forEach((id) => conditions.push({ owner: id }));
        if (createdBy) conditions.push({ createdBy });
        if (legacyOwner && !owners.includes(legacyOwner)) conditions.push({ owner: legacyOwner });

        if (!conditions.length) return [];

        let rows = conditions.length === 1
            ? await this.getObjectsByQuery(conditions[0])
            : await this.getObjectsByQuery({ $or: conditions });

        if ((!rows || rows.length === 0) && conditions.length > 1) {
            const results = await Promise.all(conditions.map((condition) => this.getObjectsByQuery(condition)));
            rows = results.flat();
        }

        const seen = new Set();
        return (rows || []).filter((object) => object?._id && !seen.has(object._id) && seen.add(object._id));
    },
    async getItemsForObject(object) {
        return apiClient.get('/api/mm-items?q={"object":"' + object + '"}').then(async result => {
            return result.data
        }).catch(error => {
            console.error("Error fetching items for object:", error);
            return [];
        });
    },
    async getSessionsForInstructorAndUser(inst, user) {
        return apiClient.get('/api/mm-sessions?q={"instructor":"' + inst + '", "user":"' + user + '"}').then(async result => {
            return normalizeSessionListResults(result.data)
        }).catch(error => {
            console.error("Error fetching sessions for instructor and user:", error);
            return null;
        });
    },

    async getCheckpointSettings(toolboxId, pointId) {
        return apiClient.get('/api/mm-checkpoint-settings?q={"toolboxId":"' + toolboxId + '", "checkpointId":"' + pointId + '"}&sort=_changed&dir=-1', { skipOverlay: true }).then(result => {
            if (!result.data || result.data.length < 1) {
                return {};
            }
            const withProposalImages = result.data.find(row => {
                if (!row.proposalImages) return false;
                if (Array.isArray(row.proposalImages)) return row.proposalImages.length > 0;
                return row.proposalImages !== '[]';
            });
            return withProposalImages || result.data[0]
        }).catch(error => {
            console.error("Error fetching checkpoint settings:", error);
            return [];
        });
    },
    async updateCheckpointSettings(measurement) {
        return apiClient.put("/api/mm-checkpoint-settings/" + measurement._id, measurement, { skipOverlay: true }).then(result => {
            return result.data
        }).catch(error => {
            console.error("Error updating checkpoint settings:", error);
            return null;
        });
    },
    async saveCheckpointSettings(measurement) {
        return apiClient.post("/api/mm-checkpoint-settings", measurement, { skipOverlay: true }).then(result => {
            return result.data
        }).catch(error => {
            console.error("Error saving checkpoint settings:", error);
            return null;
        });
    },

    /* Results */
    async getResultForSessionAndPoint(sessionId, pointId) {
        return apiClient.get('/api/mm-inspectionpoint-results?q={"sessionId":"' + sessionId + '", "checkpointId":"' + pointId + '"}&sort=_changed&dir=-1').then(result => {
            if (!result.data || result.data.length < 1) {
                return null;
            }
            return result.data[0]
        }).catch(error => {
            console.error("Error fetching result for session and point:", error);
            return null;
        });
    },
    async updateResult(resultObj) {
        return apiClient.put("/api/mm-inspectionpoint-results/" + resultObj._id, resultObj).then(result => {
            return result.data
        }).catch(error => {
            console.error("Error updating result:", error);
            return null;
        });
    },
    async saveResult(resultObj) {
        return apiClient.post("/api/mm-inspectionpoint-results", resultObj).then(result => {
            return result.data
        }).catch(error => {
            console.error("Error saving result:", error);
            return null;
        });
    },

    /* specifications */
    async getToolbox(caseId) {
        return apiClient.get('/api/mm-toolboxes/' + caseId).then(result => {
            return result.data
        }).catch(error => {
            console.error("Error fetching toolbox:", error);
            return [];
        });
    },

    async getWorkpiecesForCase(caseId) {
        return apiClient.get('/api/mm-workpieces?q={"toolbox":"' + caseId + '"}&sort=title&dir=1').then(result => {
            return result.data
        }).catch(error => {
            console.error("Error fetching workpieces for toolbox:", error);
            return [];
        });
    },
    async getCheckpoints(workpiece) {
        return apiClient.get('/api/mm-checkpoints?q={"workpiece":"' + workpiece + '"}&sort=number&dir=1').then(result => {
            return result.data
        }).catch(error => {
            console.error("Error fetching checkpoints:", error);
            return [];
        });
    },
    async updateCheckpointMarker(checkpointId, markerArray, markerField = 'marker') {
        const payload = {
            [markerField]: JSON.stringify(markerArray)
        };
        return apiClient.patch("/api/mm-checkpoints/" + checkpointId, payload).then(result => {
            return result.data
        }).catch(error => {
            console.error("Error updating checkpoint marker:", error);
            return null;
        });
    },
}
